/**
 * PATH: packages/ui/src/components/find/InPageFindBar.tsx
 * FILE_ID: UI-FIND-BAR-001
 * PURPOSE: The floating "mirror window" find widget — a small fixed
 *          top-right bar (query input + "n/m" count + prev/next + close)
 *          that mirrors the browser's native Find, but scoped to the
 *          in-app surface (email body, kanban board) that opened it.
 * WHY:     Cmd+F was opening a palette, never an in-content find. This
 *          is the visible affordance for the in-page find: type → all
 *          matches highlight in the content, Enter / arrows cycle, Esc
 *          closes. Pure presentational + callbacks (the state lives in
 *          useInPageFind); no data fetching.
 * FLOW: (query, count, activeIndex) → render bar ; key/click → callbacks.
 * DEPENDENCIES:
 *   - lucide-react: chevron + x icons.
 *   - ../../lib/utils (cn): class merge.
 */
import { useEffect, useRef } from 'react'
import { ChevronDown, ChevronUp, X } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface InPageFindBarProps {
  /** Whether the bar is shown. When false the component renders null. */
  open: boolean
  /** Current query string (controlled). */
  query: string
  /** Called on every input change. */
  onQueryChange: (value: string) => void
  /** Total hits + the active (1-based for display) position. */
  count: number
  activeIndex: number
  /** Cycle controls. */
  onNext: () => void
  onPrev: () => void
  /** Close the bar (also clears the query in useInPageFind). */
  onClose: () => void
  /** Optional surface label, e.g. 'email' / 'board' — shown as the
   *  input placeholder suffix ("Find in email…"). */
  label?: string
  className?: string
}

/**
 * Presentational find bar. Auto-focuses + selects its input when it
 * opens, and owns the local key handling (Enter/Shift+Enter, arrows,
 * Escape). All match logic lives upstream in useInPageHighlight.
 */
export function InPageFindBar({
  open,
  query,
  onQueryChange,
  count,
  activeIndex,
  onNext,
  onPrev,
  onClose,
  label,
  className,
}: InPageFindBarProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      // Focus + select on open so a re-trigger (⌘F again) lets the
      // operator immediately overtype the previous query.
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [open])

  if (!open) return null

  const hasQuery = query.trim().length > 0
  const display = count === 0 ? (hasQuery ? '0/0' : '') : `${activeIndex + 1}/${count}`

  return (
    <div
      role="search"
      data-testid="in-page-find-bar"
      className={cn(
        'fixed right-6 top-20 z-[60] flex items-center gap-1 rounded-lg border border-border',
        'bg-popover/95 px-2 py-1.5 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-popover/80',
        className,
      )}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          e.stopPropagation()
          onClose()
        } else if (e.key === 'Enter') {
          e.preventDefault()
          e.stopPropagation()
          if (e.shiftKey) onPrev()
          else onNext()
        } else if (e.key === 'ArrowDown') {
          e.preventDefault()
          onNext()
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          onPrev()
        }
      }}
    >
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={label ? `Find in ${label}…` : 'Find…'}
        aria-label={label ? `Find in ${label}` : 'Find on page'}
        className="w-44 bg-transparent px-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
      />
      <span
        data-testid="in-page-find-count"
        className="min-w-[2.75rem] select-none text-right text-xs tabular-nums text-muted-foreground"
      >
        {display}
      </span>
      <div className="flex items-center">
        <button
          type="button"
          onClick={onPrev}
          disabled={count === 0}
          title="Previous match (Shift+Enter)"
          aria-label="Previous match"
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
        >
          <ChevronUp className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={count === 0}
          title="Next match (Enter)"
          aria-label="Next match"
          className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={onClose}
          title="Close (Esc)"
          aria-label="Close find"
          className="ml-0.5 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
